import { useState } from "react";
import { authClient } from "@/lib/auth-client";
import { Skeleton } from "@/components/skeleton";

// ---------------------------------------------------------------------------
// Provider SVG icons as React components (no dangerouslySetInnerHTML)
// ---------------------------------------------------------------------------

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <circle cx="12" cy="12" r="11" fill="#1877F2" />
      <path d="M16.67 15.47l.55-3.47H13.9v-2.25c0-.95.47-1.87 1.95-1.87h1.51V5.01s-1.37-.23-2.68-.23c-2.74 0-4.53 1.66-4.53 4.67V12H7.08v3.47h3.07V23.8a12.2 12.2 0 003.77 0V15.47h2.75z" fill="#fff" />
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <defs>
        <radialGradient id="ig-auth-grad" cx="30%" cy="107%" r="150%">
          <stop offset="0%" stopColor="#fdf497" />
          <stop offset="5%" stopColor="#fdf497" />
          <stop offset="45%" stopColor="#fd5949" />
          <stop offset="60%" stopColor="#d6249f" />
          <stop offset="90%" stopColor="#285AEB" />
        </radialGradient>
      </defs>
      <rect x="1" y="1" width="22" height="22" rx="6" fill="url(#ig-auth-grad)" />
      <circle cx="12" cy="12" r="4.5" fill="none" stroke="#fff" strokeWidth="1.8" />
      <circle cx="17.5" cy="6.5" r="1.2" fill="#fff" />
      <rect x="1" y="1" width="22" height="22" rx="6" fill="none" stroke="#fff" strokeWidth="1.8" />
    </svg>
  );
}

function MicrosoftIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
      <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Provider config
// ---------------------------------------------------------------------------

const PROVIDERS = [
  { id: "google", label: "Google", icon: GoogleIcon },
  { id: "facebook", label: "Facebook", icon: FacebookIcon },
  { id: "instagram", label: "Instagram", icon: InstagramIcon },
  { id: "microsoft", label: "Microsoft", icon: MicrosoftIcon },
] as const;

type ProviderId = "google" | "facebook" | "instagram" | "microsoft";

interface Props {
  mode: "signin" | "signup";
  onError?: (message: string) => void;
}

export function SocialAuthButtons({ mode, onError }: Props) {
  const [loading, setLoading] = useState<ProviderId | null>(null);

  const handleSocial = async (providerId: ProviderId) => {
    setLoading(providerId);
    try {
      await authClient.signIn.social({
        provider: providerId,
        callbackURL: "/",
      });
    } catch (err) {
      onError?.(err instanceof Error ? err.message : "Social login failed. Please try again.");
      setLoading(null);
    }
  };

  const label = mode === "signin" ? "Continue" : "Sign up";

  return (
    <div className="space-y-3">
      {PROVIDERS.map((provider) => (
        <button
          key={provider.id}
          type="button"
          onClick={() => handleSocial(provider.id)}
          disabled={loading !== null}
          className="flex w-full items-center justify-center gap-3 rounded-lg border border-border bg-background px-4 py-2.5 text-sm font-medium text-[var(--color-foreground)] shadow-sm transition-colors hover:bg-[var(--color-surface)] disabled:opacity-50"
        >
          <provider.icon className="w-5 h-5" />
          {loading === provider.id ? (
            <>
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-4 w-24" />
            </>
          ) : (
            `${label} with ${provider.label}`
          )}
        </button>
      ))}
    </div>
  );
}
